import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { X } from 'lucide-react';

const SIZES = {
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
};

/**
 * `dismissable = false` disables Escape/backdrop/X-button close — use while a
 * submit from inside the modal is in flight. Without this, a slow request
 * lets someone close the modal thinking it failed and resubmit, firing the
 * action twice (e.g. two workspaces from one "New workspace" click if the
 * first request just hadn't resolved yet).
 */
export default function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  dismissable = true,
  size = 'md',
}) {
  useEffect(() => {
    if (!open || !dismissable) return undefined;
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, dismissable, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm animate-fade-in"
      onMouseDown={(e) => dismissable && e.target === e.currentTarget && onClose?.()}
      role="dialog"
      aria-modal="true"
    >
      {/* max-h-full + a scrolling body: a modal taller than the viewport used to
          overflow this fixed, unscrollable backdrop, putting its first and last
          rows out of reach. Header and footer stay pinned; only the body moves. */}
      <div
        className={clsx(
          'flex max-h-full w-full flex-col rounded-2xl border border-slate-200 bg-slate-100 shadow-lg animate-scale-in',
          SIZES[size] ?? SIZES.md,
        )}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-slate-200 px-6 py-4">
          <h2 className="text-base font-semibold text-slate-800">{title}</h2>
          <button
            onClick={onClose}
            disabled={!dismissable}
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        {/* min-h-0 lets this flex child shrink below its content height so it scrolls. */}
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">{children}</div>
        {footer && (
          <div className="flex shrink-0 justify-end gap-2 border-t border-slate-100 px-6 py-4">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
